import { useCallback } from "react"
import { useFetcher } from "react-router"
import { SharedRecordListTab, type RecordListState } from "../shared/SharedRecordList"
import type { ActionResponse } from "@/utils/types"
import type { LoaderData, RecordListConfig } from "./brand-ui-config"

interface RecordListTabProps {
  data: LoaderData
  config: RecordListConfig | undefined
}

export function RecordListTab({ config, data }: RecordListTabProps) {
  const fetcher = useFetcher<ActionResponse>()
  const pendingAction = (fetcher.formData?.get("action") as "SAVE_SECTION" | "REMOVE_SECTION" | null) ?? null

  const handleSave = useCallback(
    (state: RecordListState) => {
      const recordListData: RecordListConfig = {
        foldersViewEnabled: state.foldersViewEnabled,
        summariser: { enabled: state.summariserEnabled },
        showSearch: state.showSearch,
        userTags: state.userTags,
        evidenceCounts: state.evidenceCounts,
        autoTags: state.autoTags,
      }

      const formData = new FormData()
      formData.append("action", "SAVE_SECTION")
      formData.append("organisation", data.loadedOrganisation)
      formData.append("brand", data.loadedBrand ?? "")
      formData.append("jsonPath", "$.recordList")
      formData.append("value", JSON.stringify(recordListData))
      formData.append("concurrency", data.config?.concurrency ?? "")

      fetcher.submit(formData, { method: "post" })
    },
    [data, fetcher],
  )

  const handleRemove = useCallback(() => {
    const formData = new FormData()
    formData.append("action", "REMOVE_SECTION")
    formData.append("organisation", data.loadedOrganisation)
    formData.append("brand", data.loadedBrand ?? "")
    formData.append("jsonPath", "$.recordList")
    formData.append("concurrency", data.config?.concurrency ?? "")

    fetcher.submit(formData, { method: "post" })
  }, [fetcher, data])

  return (
    <SharedRecordListTab
      initialConfig={{
        userTags: config?.userTags ?? [],
        evidenceCounts: config?.evidenceCounts ?? [],
        autoTags: config?.autoTags ?? [],
        foldersViewEnabled: config?.foldersViewEnabled ?? false,
        showSearch: config?.showSearch ?? false,
        summariserEnabled: config?.summariser?.enabled ?? false,
      }}
      onSave={handleSave}
      onRemove={handleRemove}
      isFetching={fetcher.state !== "idle"}
      currentAction={pendingAction}
      actionResult={fetcher.data}
      tagConfigType="brand-ui-config"
      data={{
        loadedOrganisation: data.loadedOrganisation,
        loadedBrand: data.loadedBrand,
        evidenceCountData: data.evidenceCountData,
        userTagTypes: data.userTagTypes,
        autoTagTypes: data.autoTagTypes ?? [],
      }}
    />
  )
}
